import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../../utils/axiosConfig";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "./ValveTypeParameters.css";

const INPUT_TYPES = ["Dropdown", "Text", "Number"];
const defaultForm = { name: "", input_type: "Dropdown" };

// Grid columns: drag | name | type | count | actions
const GRID = "40px 1fr 120px 100px auto";

function ValveTypeParameters() {
  const { valveTypeId } = useParams();
  const navigate = useNavigate();
  const [valveType, setValveType] = useState(null);
  const [parameters, setParameters] = useState([]);
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editParam, setEditParam] = useState(null);
  const [paramToDelete, setParamToDelete] = useState(null);
  const [formData, setFormData] = useState(defaultForm);
  const [typeChangeWarning, setTypeChangeWarning] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const [showCheckboxModal, setShowCheckboxModal] = useState(false);
  const [currentParameter, setCurrentParameter] = useState(null);
  const [availableStandards, setAvailableStandards] = useState([]);
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [loadingStandards, setLoadingStandards] = useState(false);

  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameParam, setRenameParam] = useState(null);
  const [newParamName, setNewParamName] = useState("");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  useEffect(() => { fetchData(); }, [valveTypeId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [vtRes, paramsRes] = await Promise.all([
        axios.get(`/valve-types/${valveTypeId}`),
        axios.get(`/valve-types/${valveTypeId}/parameters`),
      ]);
      const sorted = [...(paramsRes.data.data || [])].sort((a, b) => {
        const ao = a.display_order ?? 0, bo = b.display_order ?? 0;
        return ao - bo || a.id - b.id;
      });
      setValveType(vtRes.data.data);
      setParameters(sorted);
      setHasOrderChanges(false);
    } catch (err) {
      showMessage("error", err.response?.status === 401
        ? "Session expired. Please login again."
        : err.response?.data?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 5000);
  };

  const openAddModal = () => {
    setEditParam(null); setFormData(defaultForm);
    setTypeChangeWarning(false); setShowModal(true);
  };

  const openEditModal = (param) => {
    setEditParam(param);
    setFormData({ name: param.name, input_type: param.input_type });
    setTypeChangeWarning(false); setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "input_type" && editParam?.input_type === "Dropdown" && value !== "Dropdown") {
      setTypeChangeWarning(true);
    } else if (name === "input_type") {
      setTypeChangeWarning(false);
    }
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (typeChangeWarning) {
      if (!window.confirm("Changing input type will permanently delete all existing values. Are you sure?")) return;
    }
    try {
      if (editParam) {
        await axios.put(`/valve-types/${valveTypeId}/parameters/${editParam.id}`,
          { name: formData.name, input_type: formData.input_type });
        showMessage("success", "Parameter updated successfully");
      } else {
        await axios.post(`/valve-types/${valveTypeId}/parameters`,
          { name: formData.name, input_type: formData.input_type });
        showMessage("success", "Parameter added successfully");
      }
      setShowModal(false); fetchData();
    } catch (err) {
      showMessage("error", err.response?.status === 401
        ? "Session expired. Please login again."
        : err.response?.data?.message || "Error occurred");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/valve-types/${valveTypeId}/parameters/${paramToDelete.id}`);
      showMessage("success", "Parameter deleted successfully");
      setParamToDelete(null); fetchData();
    } catch (err) {
      showMessage("error", err.response?.status === 401
        ? "Session expired. Please login again."
        : err.response?.data?.message || "Failed to delete parameter");
    }
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setParameters((prev) => {
      const oi = prev.findIndex((p) => p.id === active.id);
      const ni = prev.findIndex((p) => p.id === over.id);
      return arrayMove(prev, oi, ni);
    });
    setHasOrderChanges(true);
  };

  const handleSaveOrder = async () => {
    try {
      setSavingOrder(true);
      await axios.put(`/valve-types/${valveTypeId}/parameters/order`, {
        parameters: parameters.map((p, i) => ({ id: p.id, display_order: i + 1 })),
      });
      showMessage("success", "Parameter order saved successfully");
      setHasOrderChanges(false); fetchData();
    } catch (err) {
      showMessage("error", err.response?.status === 401
        ? "Session expired. Please login again."
        : err.response?.data?.message || "Failed to save order");
    } finally {
      setSavingOrder(false);
    }
  };

  const handleEditParam = async (param) => {
    if (param.input_type === "Dropdown") {
      if (isClassOrSize(param)) { await openCheckboxModal(param); }
      else { navigate(`/admin/valve-configuration/${valveTypeId}/parameters/${param.id}/values`); }
    } else {
      openEditModal(param);
    }
  };

  const openCheckboxModal = async (param) => {
    setCurrentParameter(param); setLoadingStandards(true); setShowCheckboxModal(true);
    try {
      const [stdRes, curRes] = await Promise.all([
        axios.get(`/standards/${param.name.toLowerCase()}`),
        axios.get(`/valve-type-parameters/${param.id}/values`),
      ]);
      const standards = stdRes.data.data || [];
      const curTexts = (curRes.data.data || []).map((v) => v.value.toLowerCase());
      setAvailableStandards(standards);
      setSelectedStandards(standards.filter((s) => curTexts.includes(s.value.toLowerCase())).map((s) => s.value));
    } catch (err) {
      showMessage("error", err.response?.data?.message || "Failed to load standards");
      setShowCheckboxModal(false);
    } finally {
      setLoadingStandards(false);
    }
  };

  const handleCheckboxToggle = (value) =>
    setSelectedStandards((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );

  const handleSaveSelectedValues = async () => {
    try {
      await axios.put(`/valve-type-parameters/${currentParameter.id}/values/bulk`,
        { values: selectedStandards });
      showMessage("success", "Values updated successfully");
      setShowCheckboxModal(false); fetchData();
    } catch (err) {
      showMessage("error", err.response?.status === 401
        ? "Session expired. Please login again."
        : err.response?.data?.message || "Failed to update values");
    }
  };

  const openRenameModal = (param) => {
    setRenameParam(param); setNewParamName(param.name); setShowRenameModal(true);
  };

  const handleRenameSubmit = async (e) => {
    e.preventDefault();
    if (!newParamName.trim()) { showMessage("error", "Parameter name cannot be empty"); return; }
    try {
      await axios.put(`/valve-types/${valveTypeId}/parameters/${renameParam.id}`,
        { name: newParamName.trim(), input_type: renameParam.input_type });
      showMessage("success", "Parameter renamed successfully");
      setShowRenameModal(false); setRenameParam(null); setNewParamName(""); fetchData();
    } catch (err) {
      showMessage("error", err.response?.status === 401
        ? "Session expired. Please login again."
        : err.response?.data?.message || "Failed to rename parameter");
    }
  };

  const isClassOrSize = (param) => {
    const n = param.name.toLowerCase();
    return n === "class" || n === "size";
  };

  if (loading) {
    return (
      <div className="valve-params-container">
        <div className="loading-container">Loading...</div>
      </div>
    );
  }

  /* ── Sortable row ──────────────────────────────────────────── */
  const SortableRow = ({ param }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
      useSortable({ id: param.id });

    return (
      <div
        ref={setNodeRef}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
          opacity: isDragging ? 0.6 : 1,
          gridTemplateColumns: GRID,
        }}
        className={`param-row${isDragging ? " dragging" : ""}`}
      >
        {/* Drag */}
        <div className="param-cell drag-cell">
          <button type="button" className="drag-handle"
            {...attributes} {...listeners}
            aria-label={`Reorder ${param.name}`} title="Drag to reorder">
            ≡
          </button>
        </div>

        {/* Name */}
        <div className="param-cell param-name-cell" title={param.name}>
          {param.name}
        </div>

        {/* Type */}
        <div className="param-cell">
          <span className={`type-badge type-${param.input_type.toLowerCase()}`}>
            {param.input_type}
          </span>
        </div>

        {/* Count */}
        <div className="param-cell">
          {param.input_type === "Dropdown"
            ? <span className="parameter-count-badge">{param.values_count}</span>
            : <span className="text-muted">—</span>}
        </div>

        {/* Actions */}
        <div className="param-cell actions-cell">
          <div className="action-buttons">
            {param.input_type === "Dropdown" && (
              <button className="btn-values"
                onClick={() => isClassOrSize(param)
                  ? openCheckboxModal(param)
                  : navigate(`/admin/valve-configuration/${valveTypeId}/parameters/${param.id}/values`)}
                title="Manage dropdown values">
                Values
              </button>
            )}
            <button className="btn-edit" onClick={() => handleEditParam(param)}>
              Edit
            </button>
            {!isClassOrSize(param) && (
              <button className="btn-rename" onClick={() => openRenameModal(param)}
                title="Rename parameter">
                Rename
              </button>
            )}
            <button className="btn-delete" onClick={() => setParamToDelete(param)}>
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* ── Render ────────────────────────────────────────────────── */
  return (
    <div className="valve-params-container">

      {/* Header */}
      <div className="valve-params-header">
        <div>
          <button className="btn-back" onClick={() => navigate("/admin/valve-configuration")}>
            ← Back to Valve Types
          </button>
          <nav className="breadcrumb">Valve Configuration &gt; {valveType?.name}</nav>
          <h1 className="valve-params-title">{valveType?.name} — Parameter List</h1>
        </div>
        <div className="valve-params-actions">
          <button className="btn-save-order" onClick={handleSaveOrder}
            disabled={!hasOrderChanges || savingOrder}
            title={hasOrderChanges ? "Save the new parameter order" : "Drag parameters to enable"}>
            {savingOrder ? "Saving..." : "Save Order"}
          </button>
          <button className="btn-add-param" onClick={openAddModal}>
            + Add Parameter
          </button>
        </div>
      </div>

      {/* Alert */}
      {message.text && (
        <div className={`alert-message alert-${message.type}`}>{message.text}</div>
      )}

      {/* Table */}
      <div className="table-card">
        <div className="table-responsive">

          {/* Header row */}
          <div className="params-grid" style={{ gridTemplateColumns: GRID }}>
            <div className="params-header" />
            <div className="params-header">Parameter Name</div>
            <div className="params-header">Input Type</div>
            <div className="params-header">Values</div>
            <div className="params-header">Actions</div>
          </div>

          {/* Body */}
          {parameters.length === 0 ? (
            <div className="no-data-row">
              No parameters found. Click "+ Add Parameter" to get started.
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={parameters.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                <div className="params-rows">
                  {parameters.map((param) => <SortableRow key={param.id} param={param} />)}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">{editParam ? "Edit Parameter" : "Add Parameter"}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Parameter Name <span className="required-mark">*</span></label>
                <input className="form-input" name="name" value={formData.name}
                  onChange={handleInputChange} placeholder="Enter parameter name"
                  required maxLength={150} autoFocus />
                <span className="help-text">Max 150 characters. Must be unique (case-insensitive).</span>
              </div>
              <div className="form-group">
                <label className="form-label">Input Type <span className="required-mark">*</span></label>
                <div className="radio-group">
                  {INPUT_TYPES.map((type) => (
                    <label key={type} className="radio-label">
                      <input type="radio" name="input_type" value={type}
                        checked={formData.input_type === type} onChange={handleInputChange} />
                      <span>{type}</span>
                    </label>
                  ))}
                </div>
                {typeChangeWarning && (
                  <div className="type-change-warning">
                    Changing input type will permanently delete all existing values. Click "Confirm & Update" to proceed.
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button className="btn-cancel" type="button" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="btn-submit" type="submit">
                  {editParam ? (typeChangeWarning ? "Confirm & Update" : "Update") : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation ── */}
      {paramToDelete && (
        <div className="modal-overlay" onClick={() => setParamToDelete(null)}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h2 className="confirm-title">Confirm Deletion</h2>
            <p className="confirm-text">
              Are you sure you want to delete "<strong>{paramToDelete.name}</strong>"?
            </p>
            {paramToDelete.values_count > 0 && (
              <div className="confirm-warning">
                This parameter has {paramToDelete.values_count} value(s). If used in submitted TCDS records, deletion will be blocked.
              </div>
            )}
            <div className="confirm-actions">
              <button className="btn-cancel" onClick={() => setParamToDelete(null)}>Cancel</button>
              <button className="btn-confirm-delete" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Class/Size Checkbox Modal ── */}
      {showCheckboxModal && (
        <div className="modal-overlay" onClick={() => setShowCheckboxModal(false)}>
          <div className="modal-content checkbox-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Select {currentParameter?.name} Values</h2>
            {loadingStandards ? (
              <div className="loading-container">Loading standards...</div>
            ) : (
              <>
                <div className="form-group">
                  <p className="help-text" style={{ marginBottom: "12px" }}>
                    Select values to use as dropdown options for {currentParameter?.name}:
                  </p>
                  <div className="checkbox-list">
                    {availableStandards.length === 0 ? (
                      <p className="text-muted">No standards available in the master list.</p>
                    ) : (
                      availableStandards.map((s) => (
                        <label key={s.id} className="checkbox-label">
                          <input type="checkbox"
                            checked={selectedStandards.includes(s.value)}
                            onChange={() => handleCheckboxToggle(s.value)} />
                          <span>{s.value}</span>
                        </label>
                      ))
                    )}
                  </div>
                  <div className="checkbox-count">Selected: {selectedStandards.length} value(s)</div>
                </div>
                <div className="modal-actions">
                  <button className="btn-cancel" type="button" onClick={() => setShowCheckboxModal(false)}>Cancel</button>
                  <button className="btn-submit" type="button" onClick={handleSaveSelectedValues}>Save Selected Values</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Rename Modal ── */}
      {showRenameModal && renameParam && (
        <div className="modal-overlay" onClick={() => setShowRenameModal(false)}>
          <div className="modal-content rename-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Rename Parameter</h2>
            <form onSubmit={handleRenameSubmit}>
              <div className="form-group">
                <label className="form-label">Current Name</label>
                <input type="text" className="form-input form-input--disabled"
                  value={renameParam.name} disabled />
              </div>
              <div className="form-group">
                <label className="form-label">New Name <span className="required-mark">*</span></label>
                <input type="text" className="form-input" value={newParamName}
                  onChange={(e) => setNewParamName(e.target.value)}
                  placeholder="Enter new parameter name" required autoFocus />
              </div>
              <div className="modal-actions">
                <button className="btn-cancel" type="button" onClick={() => {
                  setShowRenameModal(false); setRenameParam(null); setNewParamName("");
                }}>Cancel</button>
                <button className="btn-submit" type="submit">Rename</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}

export default ValveTypeParameters;